import React, { Component } from "react";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import { Grid } from "semantic-ui-react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import {
  listDocumentsInContext,
  deleteDocumentWithIdentifier,
  updateContextWithIdentifier
} from "../../actions/fs";
import { selectDocument } from "../../actions/tasklist";
import OrganizerStructureBrowser from "./OrganizerStructureBrowser";
import OrganizerFileBrowser from "./OrganizerFileBrowser";
import OrganizerViewControl from "./OrganizerViewControl";

/**
 * Import organizer-specific stylesheets
 */
import "./Organizer.css";

/**
 * Organizer : Component
 *
 * (c) Stefan Wittwer 2018 | Alle Rechte vorbehalten.
 *
 * Organizer module which allows the user to organize
 * and navigate their contexts and files
 */
class Organizer extends Component {
  state = {
    /**
     * Identifier of the selected context in the Organizer
     */
    selectedContext: null,
    /**
     * Current view mode identifier
     */
    currentViewMode: "grid"
  };
  /**
   * componentDidMount: ()
   * Fired when the Organizer is mounted, used to re-fetch
   * the documents
   */
  componentDidMount() {}
  /**
   * setContextName: newName => ()
   * Updates the display name of the current context
   */
  setContextName = newName => {
    if (!this.state.selectedContext) return;

    const update = { displayName: newName };
    this.props.updateContextWithIdentifier(this.state.selectedContext, update);
  };
  /**
   * setViewMode: viewMode => ()
   * Updates the current view mode
   */
  setViewMode = viewMode => {
    this.setState({
      currentViewMode: viewMode
    });
  };
  /**
   * selectContext: context => ()
   * Selects the specified context in the Organizer.
   * This does not dispatch an application-wide a context switch.
   */
  selectContext = context => {
    this.setState({
      selectedContext: context
    });
    this.props.listDocumentsInContext(context);
  };
  /**
   * openDocument: document => ()
   * Opens the specified document and switches to its context.
   */
  openDocument = document => {
    this.props.selectDocument(document.id, document.contextIdentifier);
    this.props.history.push("/document");
  };
  /**
   * removeDocument: document => ()
   * Removes a specified document.
   */
  removeDocument = document => {
    this.props.deleteDocumentWithIdentifier(
      document.id,
      document.contextIdentifier
    );
  };
  /**
   * filesInSpecifiedContext: context => ()
   * Returns an array of files that should be displayed in the
   * Organizer for a given context identifier.
   */
  filesInSpecifiedContext = context => {
    if (!context) return [];
    if (!this.props.files.hasOwnProperty(context)) return [];

    return this.props.files[context];
  };
  /**
   * currentlySelectedContext: () => context
   * Returns the current context object or null
   */
  currentlySelectedContext = () => {
    if (!this.state.selectedContext) return null;

    const selected = this.props.contexts.filter(
      context => context.id === this.state.selectedContext
    );

    if (selected.length === 0) return null;

    return selected[0];
  };
  /**
   * currentLocation: () -> String?
   * Returns the current location name or null
   */
  currentLocation = () => {
    const context = this.currentlySelectedContext();
    return context ? context.displayName : null;
  };
  /**
   * render: ()
   * Renders the Organizer module
   */
  render() {
    return (
      <div className="or-wrapper">
        <OrganizerViewControl
          selectViewMode={this.setViewMode}
          currentViewMode={this.state.currentViewMode}
        />
        <h2>
          <FormattedMessage
            id="organizer.application.title"
            description="Organizer application title"
          />
        </h2>
        <div className="or-main">
          <Grid>
            <Grid.Column width={4}>
              <OrganizerStructureBrowser
                contexts={this.props.contexts}
                currentContext={this.state.selectedContext}
                isLoaded={this.props.isLoaded}
                selectContext={this.selectContext}
              />
            </Grid.Column>
            <Grid.Column width={12}>
              <OrganizerFileBrowser
                files={this.filesInSpecifiedContext(this.state.selectedContext)}
                isLoaded={this.props.isLoaded}
                currentLocation={this.currentLocation()}
                currentViewMode={this.state.currentViewMode}
                selectFile={this.openDocument}
                setContextName={this.setContextName}
                deleteFile={this.removeDocument}
                currentContext={this.state.selectedContext}
              />
            </Grid.Column>
          </Grid>
        </div>
      </div>
    );
  }
}

Organizer.propTypes = {
  /**
   * List of contexts currently accessible by the user
   */
  contexts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      displayName: PropTypes.string.isRequired
    })
  ),
  /**
   * List of loaded files ordered by context identifier
   */
  files: PropTypes.shape({}),
  /**
   * True if all contexts and all documents in the currently selected
   * contexts are loaded
   */
  isLoaded: PropTypes.bool.isRequired,
  /**
   * Identifier of the currently selected context
   */
  currentContext: PropTypes.string,
  /**
   * Actions that will be dispatched
   */
  listDocumentsInContext: PropTypes.func.isRequired,
  selectDocument: PropTypes.func.isRequired,
  deleteDocumentWithIdentifier: PropTypes.func.isRequired,
  updateContextWithIdentifier: PropTypes.func.isRequired,
  /**
   * Router actions connected through withRouter
   */
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }).isRequired
};

Organizer.defaultProps = {
  contexts: [],
  files: {},
  currentContext: null
};

const mapStateToProps = state => ({
  /**
   * Make a deep copy of the state object, instead of
   * shallow, using JSON.parse(JSON.stringify(object))
   * (Redux connect() makes a shallow copy only)
   */
  contexts: JSON.parse(JSON.stringify(state.fs.contexts || [])),
  files: JSON.parse(JSON.stringify(state.fs.documents || {})),

  isLoaded:
    !!state.fs.contexts &&
    !!state.fs.files &&
    (state.tasklist.currentContext === null ||
      state.fs.files[state.tasklist.currentContext]),

  currentContext: state.tasklist.currentContext
});

export default connect(
  mapStateToProps,
  {
    listDocumentsInContext,
    selectDocument,
    deleteDocumentWithIdentifier,
    updateContextWithIdentifier
  }
)(withRouter(Organizer));



// WEBPACK FOOTER //
// ./src/modules/organizer/Organizer.js